import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

export type BanType = 'rate_limited' | 'temp_ban' | 'permanent_ban' | null;

interface BanInfo {
  type: BanType;
  message: string;
  retryAfter?: number;
}

// Global state for ban notification
let globalSetBanInfo: ((info: BanInfo | null) => void) | null = null;

export function showBanNotification(type: BanType, message: string, retryAfter?: number) {
  if (globalSetBanInfo) {
    globalSetBanInfo({ type, message, retryAfter });
  }
}

export function hideBanNotification() {
  if (globalSetBanInfo) {
    globalSetBanInfo(null);
  }
}

export function BanNotificationProvider({ children }: { children: React.ReactNode }) {
  const [banInfo, setBanInfo] = useState<BanInfo | null>(null);
  const [countdown, setCountdown] = useState<number>(0);

  useEffect(() => {
    globalSetBanInfo = setBanInfo;
    return () => {
      globalSetBanInfo = null;
    };
  }, []);

  // Countdown timer
  useEffect(() => {
    if (banInfo?.retryAfter) {
      setCountdown(banInfo.retryAfter);
      const interval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setBanInfo(null);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [banInfo]);

  if (!banInfo) return <>{children}</>;

  const isPermaBan = banInfo.type === 'permanent_ban';
  const isTempBan = banInfo.type === 'temp_ban';
  const isRateLimited = banInfo.type === 'rate_limited';

  return (
    <>
      {children}
      {createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div 
            className={`
              relative max-w-md w-full rounded-2xl shadow-2xl overflow-hidden
              ${isPermaBan ? 'bg-gradient-to-br from-red-600 to-red-800' : ''}
              ${isTempBan ? 'bg-gradient-to-br from-orange-500 to-red-600' : ''}
              ${isRateLimited ? 'bg-gradient-to-br from-amber-500 to-orange-600' : ''}
            `}
          >
            {/* Animated background pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,_white_1px,_transparent_1px)] bg-[length:20px_20px]" />
            </div>
            
            <div className="relative p-8 text-center text-white">
              {/* Icon */}
              <div className="mx-auto w-20 h-20 mb-6 rounded-full bg-white/20 flex items-center justify-center">
                {isPermaBan && (
                  <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                )}
                {isTempBan && (
                  <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                )}
                {isRateLimited && (
                  <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
              </div>

              {/* Title */}
              <h2 className="text-2xl font-bold mb-3">
                {isPermaBan && 'Erişim Engellendi'}
                {isTempBan && 'Geçici Olarak Engellendi'}
                {isRateLimited && 'Çok Hızlısınız!'}
              </h2>

              {/* Message */}
              <p className="text-white/90 mb-4 leading-relaxed">
                {banInfo.message}
              </p>
              
              {/* Contact info for bans */}
              {(isPermaBan || isTempBan) && (
                <p className="text-white/80 text-sm mb-6 leading-relaxed">
                  Bir sorununuz var ise{' '}
                  <a href="tel:+905334845926" className="underline font-semibold hover:text-white">
                    +90 533 484 59 26
                  </a>
                  {' '}numarasından iletişime geçebilirsiniz.
                </p>
              )}

              {/* Countdown */}
              {countdown > 0 && (
                <div className="mb-6">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20">
                    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span className="font-mono font-bold text-lg">{countdown}s</span>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 justify-center">
                {!isPermaBan && countdown === 0 && (
                  <button
                    onClick={() => {
                      setBanInfo(null);
                      window.location.reload();
                    }}
                    className="px-6 py-2.5 rounded-lg bg-white text-gray-900 font-semibold hover:bg-gray-100 transition-colors"
                  >
                    Tekrar Dene
                  </button>
                )}
                {!isPermaBan && (
                  <button
                    onClick={() => setBanInfo(null)}
                    className="px-6 py-2.5 rounded-lg bg-white/20 hover:bg-white/30 font-semibold transition-colors"
                  >
                    Kapat
                  </button>
                )}
              </div>

              {/* Warning for repeat offenders */}
              {isTempBan && (
                <p className="mt-6 text-sm text-white/70">
                  Tekrarlanan ihlaller kalıcı yasaklamaya yol açabilir.
                </p>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

